import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import { OtHoursWarning, UnpaidWages } from '../interfaces/dashboard.interface';
import {
    ISalaryComponent,
    IEmployeeSalary,
    IEmployeeSalaryCreate,
    IPayrollSettings,
    IPayroll,
    IFinalizeMonthResult,
    IPayrollPageResponse,
    IPayrollRunTotals,
    IExcludedEmployee,
    IPayrollRunApproval,
    IPayrollPendingApproval,
    IPayrollVariance,
    IPayrollBatchApproval,
    IPayrollSelectionRow,
    IVoidDraftPayrollResponse,
    IGeneratePayrollResponse,
    IMyPayslipSummary,
    IMyPayslipDetail,
} from '../interfaces/payroll.interface';
import {
    IDisbursementSummaryResponse,
    IEmailRunAcceptedResponse,
    IMfsDisbursementPreview,
    IPage,
    IPayrollPaymentStatus,
    IRecordBatchRequest,
    IRecordPaymentRequest,
    IReversePaymentRequest,
    ISalaryPayment,
    ISalaryPaymentBatch,
    IVoidBatchResponse,
    JournalPostStatus,
    MfsFileFormat,
} from '../interfaces/salaryPayment';

// I5: axios responseType:'blob' applies to error responses too, so a 4xx backend body (plain text
// or JSON, per CustomExceptionHandler) arrives as a Blob rather than a string. ToastContext's
// showApiError only surfaces `error.response.data` when it is already a string — for a blob it
// silently falls back to the generic message. Read the blob back to text (unwrapping a `{message}`
// JSON body when present) and rewrite `error.response.data` in place before rethrowing, so the
// original error object (and every field callers might otherwise depend on) still propagates.
// Blob.prototype.text() is not implemented by every runtime this code executes in (notably jsdom,
// which this exact codepath is unit-tested under) — fall back to FileReader, which is.
function blobToText(blob: Blob): Promise<string> {
    if (typeof (blob as any).text === 'function') {
        return (blob as any).text();
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
}

async function rethrowBlobError(error: any): Promise<never> {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await blobToText(data);
            let message = text;
            try {
                const parsed = JSON.parse(text);
                if (parsed && typeof parsed === 'object' && typeof parsed.message === 'string') {
                    message = parsed.message;
                }
            } catch {
                // Not JSON — use the raw text body as-is.
            }
            if (message) {
                error.response.data = message;
            }
        } catch {
            // Blob unreadable (e.g. already consumed); fall through and let the generic
            // fallback message stand rather than throwing a secondary error here.
        }
    }
    throw error;
}

export const PayrollService = {
    // Salary Components
    getSalaryComponents: async (institutionId?: string): Promise<ISalaryComponent[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.SALARY_COMPONENT.BY_INSTITUTION(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    createSalaryComponent: async (data: Partial<ISalaryComponent>, institutionId?: string): Promise<ISalaryComponent> => {
        data.institutionId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.post(ENDPOINTS.SALARY_COMPONENT.BASE, data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    updateSalaryComponent: async (id: string, data: Partial<ISalaryComponent>): Promise<ISalaryComponent> => {
        const response = await axios.put(ENDPOINTS.SALARY_COMPONENT.UPDATE(id), data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    deleteSalaryComponent: async (id: string): Promise<void> => {
        await axios.delete(ENDPOINTS.SALARY_COMPONENT.DELETE(id), {
            headers: AuthService.getAuthHeader()
        });
    },

    seedDefaultSalaryComponents: async (institutionId: string): Promise<void> => {
        await axios.post(ENDPOINTS.SALARY_COMPONENT.SEED_DEFAULTS(institutionId), {}, {
            headers: AuthService.getAuthHeader()
        });
    },

    // Employee Salaries
    getEmployeeSalaries: async (institutionId?: string): Promise<IEmployeeSalary[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.EMPLOYEE_SALARY.BY_INSTITUTION(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getEmployeeSalaryForUser: async (userId: string, institutionId?: string): Promise<IEmployeeSalary> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.EMPLOYEE_SALARY.BY_USER(instId, userId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    createEmployeeSalary: async (data: IEmployeeSalaryCreate): Promise<IEmployeeSalary> => {
        const response = await axios.post(ENDPOINTS.EMPLOYEE_SALARY.BASE, data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    updateEmployeeSalary: async (id: string, data: IEmployeeSalaryCreate): Promise<IEmployeeSalary> => {
        const response = await axios.put(ENDPOINTS.EMPLOYEE_SALARY.UPDATE(id), data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // Payroll Settings
    getPayrollSettings: async (institutionId?: string): Promise<IPayrollSettings | null> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL_SETTINGS.BY_INSTITUTION(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    updatePayrollSettings: async (data: Partial<IPayrollSettings>, institutionId?: string): Promise<IPayrollSettings> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.put(ENDPOINTS.PAYROLL_SETTINGS.BY_INSTITUTION(instId), data, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getOvertimeFixedCount: async (institutionId?: string): Promise<number> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL_SETTINGS.OVERTIME_FIXED_COUNT(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data.count;
    },

    // T3b (N6): how many active PIECE_RATE employees currently have fewer than 3 qualifying
    // trailing-average months -- the cold-start warning shown alongside the toggle.
    getPieceRateColdStartCount: async (institutionId?: string): Promise<number> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL_SETTINGS.PIECE_RATE_COLD_START_COUNT(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data.count;
    },

    // Payroll
    // Audit Wave 1 Lane 3 (D9, 2026-09-08 decision note §5): /generate returns
    // { payrolls, exclusions } — the backend reports every roster member dropped from this run
    // (deactivated / soft-deleted membership or user / exited before the period) instead of
    // silently shrinking the list. Fix round 1 / H2: the FULL shape is now returned (not just
    // `.payrolls`) so the caller (PayrollList.tsx) can surface the exclusions to the operator.
    generatePayroll: async (year: number, month: number, institutionId?: string): Promise<IGeneratePayrollResponse> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.post(ENDPOINTS.PAYROLL.GENERATE, { institutionId: instId, year, month }, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getPayrollList: async (year: number, month: number, page: number = 0, size: number = 50, institutionId?: string): Promise<IPayrollPageResponse> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.LIST(instId, year, month), {
            params: { page, size },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getRunTotals: async (year: number, month: number, institutionId?: string): Promise<IPayrollRunTotals> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.RUN_TOTALS(instId, year, month), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // S4-04: unpaid finalized wages and their legal deadline, the same figures as the run list.
    getUnpaidWages: async (institutionId?: string): Promise<UnpaidWages> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.UNPAID_WAGES(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // S4-13: employees over 48/60 hours this week so far; enabled=false when the institution has the warning off.
    getOtHoursWarning: async (institutionId?: string): Promise<OtHoursWarning> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.OT_HOURS_WARNING(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // Board B-03: active members who get no payslip in this run for want of an active salary.
    getNoActiveSalaryExclusions: async (year: number, month: number, institutionId?: string): Promise<IExcludedEmployee[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.NO_SALARY_EXCLUSIONS(instId, year, month), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getRunApproval: async (year: number, month: number, institutionId?: string): Promise<IPayrollRunApproval> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.RUN_APPROVAL(instId, year, month), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getRunVariance: async (year: number, month: number, institutionId?: string): Promise<IPayrollVariance> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.RUN_APPROVAL_VARIANCE(instId, year, month), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getAwaitingApproval: async (institutionId?: string): Promise<IPayrollPendingApproval[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.AWAITING_APPROVAL(instId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getBatchApprovalStates: async (batchIds: string[], institutionId?: string): Promise<IPayrollBatchApproval[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        if (batchIds.length === 0) return [];
        const response = await axios.get(ENDPOINTS.PAYROLL.BATCH_APPROVAL(instId), {
            params: { batchIds: batchIds.join(',') },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    approvePayrollRun: async (year: number, month: number, institutionId?: string): Promise<IPayrollRunApproval> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.post(ENDPOINTS.PAYROLL.RUN_APPROVAL_APPROVE(instId, year, month), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    rejectPayrollRun: async (year: number, month: number, reason: string, institutionId?: string): Promise<IPayrollRunApproval> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.post(ENDPOINTS.PAYROLL.RUN_APPROVAL_REJECT(instId, year, month), { reason }, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getFinalizedRunSelection: async (year: number, month: number, institutionId?: string): Promise<IPayrollSelectionRow[]> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.FINALIZED_SELECTION(instId, year, month), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getPayrollById: async (id: string): Promise<IPayroll> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.BY_ID(id), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    retryAccrual: async (id: string): Promise<IPayroll> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.RETRY_ACCRUAL(id), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    finalizePayroll: async (id: string): Promise<IPayroll> => {
        const response = await axios.patch(ENDPOINTS.PAYROLL.FINALIZE(id), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    finalizeAllPayroll: async (year: number, month: number, institutionId?: string): Promise<IFinalizeMonthResult | undefined> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.patch(ENDPOINTS.PAYROLL.FINALIZE_ALL(instId, year, month), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response?.data;
    },

    // D1: void every DRAFT row for the month; FINALIZED rows are untouched. The existing
    // Generate Payroll button then serves as the regenerate.
    voidDraftMonth: async (year: number, month: number, institutionId?: string): Promise<IVoidDraftPayrollResponse> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.delete(ENDPOINTS.PAYROLL.VOID_DRAFT(instId, year, month), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    /** Employee self-service: the caller's own FINALIZED payslips, newest first. */
    listMyPayslips: async (institutionId: string, page = 0, size = 20): Promise<IPage<IMyPayslipSummary>> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.MY_PAYSLIPS(institutionId, page, size), {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    getMyPayslip: async (id: string): Promise<IMyPayslipDetail> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.MY_PAYSLIP(id), { headers: AuthService.getAuthHeader() });
        return response.data;
    },

    downloadMyPayslip: async (id: string, lang: 'EN' | 'BN', fileBase: string): Promise<void> => {
        try {
            const response = await axios.get(ENDPOINTS.PAYROLL.MY_PAYSLIP_PDF(id, lang), {
                headers: AuthService.getAuthHeader(),
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${fileBase}${lang === 'BN' ? '-bn' : ''}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            return rethrowBlobError(error);
        }
    },

    /** `fileBase` names the file (e.g. payslip-rafiq-2026-08); falls back to the payroll id. */
    downloadPayslip: async (id: string, lang?: 'EN' | 'BN', fileBase?: string): Promise<void> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.PAYSLIP(id, lang), {
            headers: AuthService.getAuthHeader(),
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileBase ?? `payslip-${id}`}${lang === 'BN' ? '-bn' : ''}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    downloadPayslipsZip: async (institutionId: string, year: number, month: number): Promise<void> => {
        try {
            const response = await axios.get(ENDPOINTS.PAYROLL.PAYSLIPS_ZIP(institutionId, year, month), {
                headers: AuthService.getAuthHeader(),
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            // Fix round 1 / M-1: a[download] overrides Content-Disposition for a blob URL, so
            // the backend's zero-padded filename (item 6) never reaches the user unless this is
            // padded too.
            link.setAttribute('download', `payslips-${year}-${String(month).padStart(2, '0')}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            return rethrowBlobError(error);
        }
    },

    downloadPayslipsCombinedPdf: async (institutionId: string, year: number, month: number): Promise<void> => {
        try {
            const response = await axios.get(ENDPOINTS.PAYROLL.PAYSLIPS_PDF(institutionId, year, month), {
                headers: AuthService.getAuthHeader(),
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            // Fix round 1 / M-1: same rationale as the ZIP filename above.
            link.setAttribute('download', `payslips-${year}-${String(month).padStart(2, '0')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            return rethrowBlobError(error);
        }
    },

    downloadBankAdviceCsv: async (batchId: string): Promise<void> => {
        try {
            const response = await axios.get(ENDPOINTS.PAYROLL.BANK_ADVICE_CSV(batchId), {
                headers: AuthService.getAuthHeader(),
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bank-advice-${batchId}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            return rethrowBlobError(error);
        }
    },

    // MFS disbursement lane (docs/decisions/2026-09-11-garments-mfs-disbursement.md §2).
    getDisbursementSummary: async (
        institutionId: string,
        year: number,
        month: number
    ): Promise<IDisbursementSummaryResponse> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.DISBURSEMENT_SUMMARY(institutionId, year, month), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getMfsDisbursementPreview: async (batchId: string, format: MfsFileFormat): Promise<IMfsDisbursementPreview> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.MFS_DISBURSEMENT_PREVIEW(batchId), {
            params: { format },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    downloadMfsDisbursementCsv: async (batchId: string, format: MfsFileFormat): Promise<void> => {
        try {
            const response = await axios.get(ENDPOINTS.PAYROLL.MFS_DISBURSEMENT_CSV(batchId), {
                params: { format },
                headers: AuthService.getAuthHeader(),
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `mfs-${format.toLowerCase()}-${batchId.substring(0, 8)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            return rethrowBlobError(error);
        }
    },

    emailPayslip: async (payrollId: string): Promise<void> => {
        await axios.post(ENDPOINTS.PAYROLL.PAYSLIP_EMAIL(payrollId), {}, {
            headers: AuthService.getAuthHeader()
        });
    },

    // T6 item 4: rendering + sending now happens off the request thread; the endpoint returns
    // 202 Accepted with a candidate count, not a synchronous sent/skipped result.
    emailRunPayslips: async (institutionId: string, year: number, month: number): Promise<IEmailRunAcceptedResponse> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.PAYSLIPS_EMAIL(institutionId, year, month), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // Salary Payments
    recordPayment: async (payrollId: string, req: IRecordPaymentRequest): Promise<ISalaryPayment> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.RECORD_PAYMENT(payrollId), req, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    reversePayment: async (paymentId: string, req: IReversePaymentRequest): Promise<ISalaryPayment> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.REVERSE_PAYMENT(paymentId), req, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getPayrollPaymentStatus: async (payrollId: string): Promise<IPayrollPaymentStatus> => {
        const response = await axios.get(ENDPOINTS.PAYROLL.PAYMENT_STATUS(payrollId), {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    retryJournalPost: async (paymentId: string): Promise<ISalaryPayment> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.RETRY_JOURNAL_POST(paymentId), {}, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    skipJournalPost: async (paymentId: string, reason: string): Promise<ISalaryPayment> => {
        const response = await axios.post(
            ENDPOINTS.PAYROLL.SKIP_JOURNAL_POST(paymentId),
            { reason },
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    // Batch payments
    recordBatch: async (req: IRecordBatchRequest): Promise<ISalaryPaymentBatch> => {
        const response = await axios.post(ENDPOINTS.PAYROLL.RECORD_BATCH, req, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    voidBatch: async (batchId: string, reason: string): Promise<IVoidBatchResponse> => {
        const response = await axios.post(
            ENDPOINTS.PAYROLL.VOID_BATCH(batchId),
            { reason },
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    listBatches: async (
        institutionId?: string,
        page: number = 0,
        size: number = 50
    ): Promise<IPage<ISalaryPaymentBatch>> => {
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.LIST_BATCHES, {
            params: { institutionId: instId, page, size },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getPaymentReconciliation: async (
        institutionId?: string,
        status: JournalPostStatus | undefined = 'FAILED',
        page: number = 0,
        size: number = 50
    ): Promise<IPage<ISalaryPayment>> => {
        // status is an INCLUDE filter; omitted = every payment not yet posted.
        const instId = institutionId || AuthService.getSelectedInstitution()!!;
        const response = await axios.get(ENDPOINTS.PAYROLL.RECONCILIATION, {
            params: { institutionId: instId, status, page, size },
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    // Segment assignment
    updatePayrollSegment: async (id: string, segmentId?: string): Promise<IPayroll> => {
        const response = await axios.put(ENDPOINTS.PAYROLL.SEGMENT(id), { segmentId: segmentId || undefined }, {
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    bulkUpdatePayrollSegment: async (payrollIds: string[], segmentId?: string): Promise<void> => {
        await axios.put(ENDPOINTS.PAYROLL.BULK_SEGMENT, { payrollIds, segmentId: segmentId || undefined }, {
            headers: AuthService.getAuthHeader()
        });
    },
};
